import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const About: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div className="container mx-auto px-4 py-12">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold text-mypa-blue mb-4">{t("about.title")}</h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t("about.subtitle")}</p>
      </div>
      
      {/* Mission and Vision */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-16">
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-mypa-blue mb-4">{t("about.mission.title")}</h2>
          <p className="text-gray-600">{t("about.mission.description")}</p>
        </div>
        <div className="bg-white rounded-lg shadow-md p-8">
          <h2 className="text-2xl font-bold text-mypa-blue mb-4">{t("about.vision.title")}</h2>
          <p className="text-gray-600">{t("about.vision.description")}</p>
        </div>
      </div>
      
      {/* Values */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-mypa-blue mb-8 text-center">{t("about.values.title")}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-mypa-green bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-mypa-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-mypa-blue mb-2">{t("about.values.innovation.title")}</h3>
            <p className="text-gray-600">{t("about.values.innovation.description")}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-mypa-green bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-mypa-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-mypa-blue mb-2">{t("about.values.adaptation.title")}</h3>
            <p className="text-gray-600">{t("about.values.adaptation.description")}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-mypa-green bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-mypa-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-mypa-blue mb-2">{t("about.values.impact.title")}</h3>
            <p className="text-gray-600">{t("about.values.impact.description")}</p>
          </div>
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="w-16 h-16 bg-mypa-green bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-mypa-green" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
              </svg>
            </div>
            <h3 className="text-xl font-bold text-mypa-blue mb-2">{t("about.values.accessibility.title")}</h3>
            <p className="text-gray-600">{t("about.values.accessibility.description")}</p>
          </div>
        </div>
      </div>
      
      {/* Team */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-mypa-blue mb-8 text-center">{t("about.team.title")}</h2>
        <div className="bg-white rounded-lg shadow-md p-8">
          <p className="text-gray-600 text-center mb-8">{t("about.team.description")}</p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Team member 1 */}
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center bg-mypa-green bg-opacity-20">
                  <span className="text-mypa-green font-medium">CEO</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-mypa-blue mb-1">Sarah Martin</h3>
              <p className="text-gray-600 mb-2">CEO & Co-fondatrice</p>
              <p className="text-sm text-gray-500">Experte en stratégie carbone et développement durable</p>
            </div>
            
            {/* Team member 2 */}
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center bg-mypa-green bg-opacity-20">
                  <span className="text-mypa-green font-medium">CTO</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-mypa-blue mb-1">Thomas Dubois</h3>
              <p className="text-gray-600 mb-2">CTO & Co-fondateur</p>
              <p className="text-sm text-gray-500">Expert en technologies vertes et analyse de données</p>
            </div>
            
            {/* Team member 3 */}
            <div className="text-center">
              <div className="w-32 h-32 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
                <div className="w-full h-full flex items-center justify-center bg-mypa-green bg-opacity-20">
                  <span className="text-mypa-green font-medium">COO</span>
                </div>
              </div>
              <h3 className="text-xl font-bold text-mypa-blue mb-1">Amina Kader</h3>
              <p className="text-gray-600 mb-2">COO</p>
              <p className="text-sm text-gray-500">Spécialiste en opérations et gestion de projets durables</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Journey */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-mypa-blue mb-8 text-center">{t("about.journey.title")}</h2>
        <div className="bg-white rounded-lg shadow-md p-8">
          <p className="text-gray-600 mb-8">{t("about.journey.description")}</p>
          
          <div className="relative">
            {/* Timeline */}
            <div className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-mypa-green"></div>
            
            {/* Timeline items */}
            <div className="relative z-10">
              {/* Item 1 */}
              <div className="mb-12 flex items-center">
                <div className="w-1/2 pr-8 text-right">
                  <h3 className="text-xl font-bold text-mypa-blue mb-2">2023</h3>
                  <p className="text-gray-600">Fondation de mypa avec une vision claire : rendre la décarbonation accessible à tous</p>
                </div>
                <div className="w-4 h-4 bg-mypa-green rounded-full z-10"></div>
                <div className="w-1/2 pl-8">
                </div>
              </div>
              
              {/* Item 2 */}
              <div className="mb-12 flex items-center">
                <div className="w-1/2 pr-8">
                </div>
                <div className="w-4 h-4 bg-mypa-green rounded-full z-10"></div>
                <div className="w-1/2 pl-8">
                  <h3 className="text-xl font-bold text-mypa-blue mb-2">2024</h3>
                  <p className="text-gray-600">Lancement de notre plateforme et premiers partenariats stratégiques</p>
                </div>
              </div>
              
              {/* Item 3 */}
              <div className="flex items-center">
                <div className="w-1/2 pr-8 text-right">
                  <h3 className="text-xl font-bold text-mypa-blue mb-2">2025</h3>
                  <p className="text-gray-600">Expansion internationale et développement de nouvelles solutions innovantes</p>
                </div>
                <div className="w-4 h-4 bg-mypa-green rounded-full z-10"></div>
                <div className="w-1/2 pl-8">
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Partners */}
      <div className="mb-16">
        <h2 className="text-3xl font-bold text-mypa-blue mb-8 text-center">{t("about.partners.title")}</h2>
        <div className="bg-white rounded-lg shadow-md p-8">
          <p className="text-gray-600 text-center mb-8">{t("about.partners.description")}</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {/* Partner logos placeholders */}
            <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
              <span className="text-gray-400">Partner 1</span>
            </div>
            <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
              <span className="text-gray-400">Partner 2</span>
            </div>
            <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
              <span className="text-gray-400">Partner 3</span>
            </div>
            <div className="h-24 bg-gray-100 rounded flex items-center justify-center">
              <span className="text-gray-400">Partner 4</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* CTA */}
      <div className="bg-mypa-blue bg-opacity-10 rounded-lg p-8 text-center">
        <h2 className="text-2xl font-bold text-mypa-blue mb-4">Prêt à commencer votre parcours de décarbonation ?</h2>
        <p className="text-gray-600 mb-6 max-w-2xl mx-auto">Contactez-nous dès aujourd'hui pour discuter de vos besoins spécifiques et découvrir comment nos solutions peuvent vous aider à atteindre vos objectifs.</p>
        <Link to="/contact" className="bg-mypa-green text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition-colors">
          Nous contacter
        </Link>
      </div>
    </div>
  );
};

export default About;
